import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { CalendarIcon, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { trpc } from '~/api/trpc';
import { Button } from '~/components/ui/button';
import { Calendar } from '~/components/ui/calendar';
import { Input } from '~/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';

dayjs.extend(isoWeek);

const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export function MyAvailabilityTab() {
    const [currentWeekStart, setCurrentWeekStart] = useState(() =>
        dayjs().startOf('isoWeek')
    );
    const [editingDay, setEditingDay] = useState<number | null>(null);
    const [startTimeInput, setStartTimeInput] = useState('');
    const [endTimeInput, setEndTimeInput] = useState('');
    const [typeInput, setTypeInput] = useState<'AVAILABLE' | 'BLOCKED'>('AVAILABLE');
    const handlePrevWeek = () => setCurrentWeekStart(prev => prev.subtract(7, 'day'));
    const handleNextWeek = () => setCurrentWeekStart(prev => prev.add(7, 'day'));
    const handleTodayClick = () => setCurrentWeekStart(dayjs().startOf('isoWeek'));

    const daysOfWeek = useMemo(
        () => Array.from({ length: 7 }, (_, i) => currentWeekStart.add(i, 'day')),
        [currentWeekStart]
    );

    const weekEndDisplay = currentWeekStart.add(6, 'day');
    const weekNumber = currentWeekStart.isoWeek();

    const utils = trpc.useUtils();

    const { data: entries, isLoading, error } = trpc.practitionerAvailabilityPreferences.getMyWeek.useQuery(
        { weekStart: currentWeekStart.format('YYYY-MM-DD') },
    );

    const upsertMutation = trpc.practitionerAvailabilityPreferences.upsert.useMutation({
        onSuccess: () => {
            utils.practitionerAvailabilityPreferences.getMyWeek.invalidate();
            setEditingDay(null);
        },
    });

    const deleteMutation = trpc.practitionerAvailabilityPreferences.delete.useMutation({
        onSuccess: () => {
            utils.practitionerAvailabilityPreferences.getMyWeek.invalidate();
        },
    });

    const entriesByDate = useMemo(() => {
        const map = new Map<string, { startTime: string; endTime: string; type: string }>();
        if (entries) {
            for (const entry of entries) {
                map.set(entry.date, { startTime: entry.startTime, endTime: entry.endTime, type: entry.type });
            }
        }
        return map;
    }, [entries]);

    const handleCellClick = (dayIndex: number) => {
        const entry = entriesByDate.get(daysOfWeek[dayIndex].format('YYYY-MM-DD'));
        setEditingDay(dayIndex);
        setStartTimeInput(entry?.startTime ?? '07:15');
        setEndTimeInput(entry?.endTime ?? '20:30');
        setTypeInput((entry?.type as 'AVAILABLE' | 'BLOCKED') ?? 'AVAILABLE');
    };

    const handleSave = () => {
        if (editingDay === null) return;
        const date = daysOfWeek[editingDay].format('YYYY-MM-DD');
        upsertMutation.mutate({ date, startTime: startTimeInput, endTime: endTimeInput, type: typeInput });
    };

    const handleDelete = (dayIndex: number) => {
        const date = daysOfWeek[dayIndex].format('YYYY-MM-DD');
        deleteMutation.mutate({ date });
    };

    const handleCancel = () => {
        setEditingDay(null);
    };

    return (
        <div>
            <div className="flex items-center gap-2 mb-6">
                <Button variant="outline" size="sm" onClick={handlePrevWeek}>
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleTodayClick}>
                    Heute
                </Button>
                <Button variant="outline" size="sm" onClick={handleNextWeek}>
                    <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium ml-2">
                    KW {weekNumber}, {currentWeekStart.format('DD.MM.')} - {weekEndDisplay.format('DD.MM.YYYY')}
                </span>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="ml-2">
                            <CalendarIcon className="h-4 w-4" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            mode="single"
                            selected={currentWeekStart.toDate()}
                            onSelect={(date) => {
                                if (date) {
                                    setCurrentWeekStart(dayjs(date).startOf('isoWeek'));
                                }
                            }}
                            weekStartsOn={1}
                        />
                    </PopoverContent>
                </Popover>
            </div>

            {error ? (
                <div className="flex items-center justify-center h-40">
                    <p className="text-muted-foreground">
                        {error.data?.code === 'PRECONDITION_FAILED'
                            ? 'Kein Behandler zugeordnet. Bitte wenden Sie sich an einen Administrator.'
                            : 'Ein Fehler ist aufgetreten.'}
                    </p>
                </div>
            ) : isLoading ? (
                <div className="flex items-center justify-center h-40">
                    <p className="text-muted-foreground">Laden...</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-300 overflow-x-auto">
                    <table className="w-full border-collapse min-w-[700px]">
                        <thead>
                            <tr className="bg-muted/50">
                                {daysOfWeek.map((day, i) => (
                                    <th key={i} className="text-center px-2 py-3 font-medium text-sm text-muted-foreground border-b-2 border-l first:border-l-0 border-gray-300">
                                        {DAY_LABELS[i]} {day.format('DD.MM.')}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {daysOfWeek.map((day, i) => {
                                    const dateKey = day.format('YYYY-MM-DD');
                                    const entry = entriesByDate.get(dateKey);
                                    const isEditing = editingDay === i;

                                    return (
                                        <td key={i} className="px-2 py-3 text-center border-l first:border-l-0 border-gray-300 align-top min-w-[100px]">
                                            {isEditing ? (
                                                <div className="flex flex-col gap-2 items-center">
                                                    <Select value={typeInput} onValueChange={(v) => setTypeInput(v as 'AVAILABLE' | 'BLOCKED')}>
                                                        <SelectTrigger className={`w-[120px] text-xs ${typeInput === 'AVAILABLE' ? 'border-green-400' : 'border-red-400'}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="AVAILABLE">Anwesenheit</SelectItem>
                                                            <SelectItem value="BLOCKED">Abwesenheit</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <Input
                                                        type="time"
                                                        value={startTimeInput}
                                                        onChange={e => setStartTimeInput(e.target.value)}
                                                        className="w-[100px] text-xs"
                                                    />
                                                    <Input
                                                        type="time"
                                                        value={endTimeInput}
                                                        onChange={e => setEndTimeInput(e.target.value)}
                                                        className="w-[100px] text-xs"
                                                    />
                                                    <div className="flex gap-1">
                                                        <Button
                                                            size="sm"
                                                            variant="default"
                                                            onClick={handleSave}
                                                            disabled={upsertMutation.isPending}
                                                            className="text-xs px-2 py-1 h-7"
                                                        >
                                                            Speichern
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={handleCancel}
                                                            className="text-xs px-2 py-1 h-7"
                                                        >
                                                            Abbrechen
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : entry ? (
                                                <div
                                                    className={`inline-flex flex-col items-center rounded px-3 py-2 border cursor-pointer transition-colors relative group ${
                                                        entry.type === 'BLOCKED'
                                                            ? 'bg-red-100 border-red-300 hover:bg-red-200'
                                                            : 'bg-green-100 border-green-300 hover:bg-green-200'
                                                    }`}
                                                    onClick={() => handleCellClick(i)}
                                                >
                                                    <span className={`text-sm font-medium ${entry.type === 'BLOCKED' ? 'text-red-800' : 'text-green-800'}`}>
                                                        {entry.startTime} - {entry.endTime}
                                                    </span>
                                                    <button
                                                        className="absolute -top-2 -right-2 bg-red-100 border border-red-300 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(i);
                                                        }}
                                                    >
                                                        <X className="h-3 w-3 text-red-600" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div
                                                    className="inline-flex items-center justify-center w-16 h-12 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
                                                    onClick={() => handleCellClick(i)}
                                                >
                                                    <Plus className="h-4 w-4 text-gray-400" />
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
